import React, { useEffect, useState } from 'react';
import { useApp } from '../contexts/AppContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Activity, ShieldCheck, Phone, Brain, Cross, Droplets, Pill, Smile, Frown, Meh, Angry, CircleDashed, Lock, AlertTriangle } from 'lucide-react';
import ExportBar from '../components/ExportBar';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function HealthTeacherView() {
  const { t, currentTeacher, lang } = useApp();
  const [health, setHealth] = useState(null);
  const [moods, setMoods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('phr');
  const [showBooking, setShowBooking] = useState(false);
  const [bf, setBf] = useState({ specialty: '', preferred_date: '', preferred_time: '', notes: '' });
  const [appts, setAppts] = useState([]);

  useEffect(() => {
    if (!currentTeacher) return;
    Promise.all([
      axios.get(`${API}/health/${currentTeacher.teacher_id}`).catch(() => ({ data: null })),
      axios.get(`${API}/health/${currentTeacher.teacher_id}/mood-history`).catch(() => ({ data: [] })),
      axios.get(`${API}/health/${currentTeacher.teacher_id}/appointments`).catch(() => ({ data: [] })),
    ]).then(([h, m, a]) => {
      setHealth(h.data); setMoods(m.data); setAppts(a.data); setLoading(false);
    });
  }, [currentTeacher]); // eslint-disable-line react-hooks/exhaustive-deps

  const logMood = async (score) => {
    if (!currentTeacher) return;
    try {
      await axios.post(`${API}/health/mood?teacher_id=${currentTeacher.teacher_id}&score=${score}`);
      toast.success(lang === 'bn' ? 'মেজাজ সংরক্ষিত' : 'Mood logged');
      const res = await axios.get(`${API}/health/${currentTeacher.teacher_id}/mood-history`);
      setMoods(res.data);
    } catch (e) { toast.error('Failed'); }
  };

  const bookAppt = async () => {
    if (!currentTeacher) return;
    try {
      await axios.post(`${API}/health/appointments`, { teacher_id: currentTeacher.teacher_id, ...bf });
      toast.success(lang === 'bn' ? 'অ্যাপয়েন্টমেন্ট নেওয়া হয়েছে' : 'Appointment booked');
      setShowBooking(false);
      const res = await axios.get(`${API}/health/${currentTeacher.teacher_id}/appointments`);
      setAppts(res.data);
    } catch (e) { toast.error('Failed'); }
  };

  if (loading) return <div className="text-center py-12" style={{color:'var(--color-label-tertiary)'}}>{t('loading')}</div>;

  const moodIcons = [Angry, Frown, CircleDashed, Meh, Smile];
  const moodColors = ['#C00000', '#E65100', '#C9A84C', '#6B8E23', '#1D9E75'];

  const healthLabels = {
    health_phr: { en: 'Health Record', bn: 'স্বাস্থ্য রেকর্ড' },
    health_mental: { en: 'Mental Wellness', bn: 'মানসিক সুস্বাস্থ্য' },
    health_fitness: { en: 'Fitness & Activity', bn: 'ফিটনেস ও কার্যকলাপ' },
    health_physical: { en: 'Physical Checkup', bn: 'শারীরিক পরীক্ষা' },
    health_telemedicine: { en: 'Telemedicine', bn: 'অনলাইন ডাক্তার' },
    health_womens: { en: "Women's Health", bn: 'মহিলাদের স্বাস্থ্য' },
    health_counselling: { en: 'Psychology Counselling', bn: 'মনোবিজ্ঞান কাউন্সেলিং' },
    health_emergency: { en: 'Emergency', bn: 'জরুরি সাহায্য' },
  };
  const tabs = [
    { id: 'phr', key: 'health_phr', icon: Activity },
    { id: 'mental', key: 'health_mental', icon: Brain },
    { id: 'fitness', key: 'health_fitness', icon: Activity },
    { id: 'physical', key: 'health_physical', icon: Cross },
    { id: 'counselling', key: 'health_counselling', icon: Brain },
    { id: 'tele', key: 'health_telemedicine', icon: Phone },
    ...((health && health.menstrual_data) ? [{ id: 'womens', key: 'health_womens', icon: Droplets }] : []),
    { id: 'emergency', key: 'health_emergency', icon: Cross },
  ];

  // Show helpful message if no health record exists
  if (!health && tab === 'phr') {
    return (
      <div className="space-y-4" data-testid="health-teacher-view">
        <div style={{ background: 'var(--color-accent)', color: 'white', padding: 'var(--space-4)', borderRadius: 'var(--radius-md)', display: 'flex', alignItems: 'flex-start', gap: 'var(--space-3)' }} data-testid="health-privacy-notice">
          <ShieldCheck size={20} style={{ flexShrink: 0, marginTop: 2 }} />
          <p style={{ fontSize: 'var(--text-subhead)', lineHeight: 1.5 }}>{t('health_privacy')}</p>
        </div>
        <div className="flex gap-2 overflow-x-auto pb-1">
          {tabs.map(s => { const Icon = s.icon; const lbl = healthLabels[s.key] || { en: s.key, bn: s.key }; return (
            <button key={s.id} data-testid={`health-tab-${s.id}`} onClick={() => setTab(s.id)}
              className="btn btn-sm" style={{ background: tab === s.id ? 'var(--color-accent)' : 'var(--color-surface-2)', color: tab === s.id ? 'white' : 'var(--color-label-secondary)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, padding: '8px 14px', minWidth: 80, fontSize: '12px', borderRadius: 'var(--radius-md)' }}>
              <Icon size={16} /><span style={{ fontWeight: 500, whiteSpace: 'nowrap' }}>{lang === 'bn' ? lbl.bn : lbl.en}</span>
              <span style={{ fontSize: '9px', opacity: 0.5, whiteSpace: 'nowrap' }}>{lang === 'bn' ? lbl.en : lbl.bn}</span>
            </button>); })}
        </div>
        <div className="card-surface" style={{ textAlign: 'center', padding: 'var(--space-10)' }}>
          <Activity size={48} style={{ color: 'var(--color-label-tertiary)', margin: '0 auto 16px' }} />
          <h3 style={{ fontSize: 'var(--text-body)', fontWeight: 600, color: 'var(--color-label-primary)' }}>{lang === 'bn' ? 'স্বাস্থ্য রেকর্ড পাওয়া যায়নি' : 'No Health Record Found'}</h3>
          <p style={{ fontSize: 'var(--text-subhead)', color: 'var(--color-label-secondary)', marginTop: 'var(--space-2)', maxWidth: 400, margin: '8px auto 0' }}>{lang === 'bn' ? 'আপনার স্বাস্থ্য তথ্য এখনো যোগ করা হয়নি। অনুগ্রহ করে স্বাস্থ্য বিভাগে যোগাযোগ করুন।' : 'Your health data has not been added yet. Please contact the health department or your school administration.'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 animate-fade-in" data-testid="health-teacher-view">
      <div style={{ background: 'var(--color-accent)', color: 'white', padding: 'var(--space-4)', borderRadius: 'var(--radius-md)', display: 'flex', alignItems: 'flex-start', gap: 'var(--space-3)' }} data-testid="health-privacy-notice">
        <ShieldCheck size={20} style={{ flexShrink: 0, marginTop: 2 }} />
        <div>
          <p style={{ fontSize: 'var(--text-subhead)', lineHeight: 1.5 }}>{t('health_privacy')}</p>
        </div>
      </div>

      <div className="flex gap-2 overflow-x-auto pb-1">
        {tabs.map(s => {
          const Icon = s.icon;
          const lbl = healthLabels[s.key] || { en: s.key, bn: s.key };
          return (
            <button key={s.id} data-testid={`health-tab-${s.id}`} onClick={() => setTab(s.id)}
              className="btn btn-sm" style={{
                background: tab === s.id ? 'var(--color-accent)' : 'var(--color-surface-2)',
                color: tab === s.id ? 'white' : 'var(--color-label-secondary)',
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
                padding: '8px 14px', minWidth: 80, fontSize: '12px', borderRadius: 'var(--radius-md)',
              }}>
              <Icon size={16} />
              <span style={{ fontWeight: 500, whiteSpace: 'nowrap' }}>{lang === 'bn' ? lbl.bn : lbl.en}</span>
              <span style={{ fontSize: '9px', opacity: tab === s.id ? 0.7 : 0.4, whiteSpace: 'nowrap' }}>{lang === 'bn' ? lbl.en : lbl.bn}</span>
            </button>
          );
        })}
      </div>

      {tab === 'phr' && health && (
        <div className="space-y-3">
          <div className="card-flat p-4">
            <h3 className="text-sm font-semibold font-bangla mb-3">{t('health_phr')}</h3>
            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 bg-slate-50 rounded-sm">
                <p className="text-[10px] text-slate-400 font-bangla">{t('health_blood')}</p>
                <p className="text-lg font-bold text-[#C00000]">{health?.blood_group || 'N/A'}</p>
              </div>
              <div className="p-3 bg-slate-50 rounded-sm">
                <p className="text-[10px] text-slate-400">SBHID</p>
                <p className="text-xs font-medium text-[#1B2A4A]">{health?.sbhid || 'N/A'}</p>
              </div>
            </div>
          </div>
          {(health.allergies || []).length > 0 && (
            <div className="card-flat p-4">
              <p className="text-xs font-medium text-slate-600 font-bangla mb-2">{t('health_allergies')}</p>
              <div className="flex gap-1.5 flex-wrap">
                {(health.allergies || []).map((a, i) => (
                  <span key={i} className="px-2 py-0.5 bg-red-50 text-red-600 text-xs rounded-sm font-bangla">{a}</span>
                ))}
              </div>
            </div>
          )}
          {(health?.chronic_conditions || []).length > 0 && (
            <div className="card-flat p-4">
              <p className="text-xs font-medium text-slate-600 font-bangla mb-2">{t('health_conditions')}</p>
              <div className="flex gap-1.5 flex-wrap">
                {(health.chronic_conditions || []).map((c, i) => (
                  <span key={i} className="px-2 py-0.5 bg-amber-50 text-amber-700 text-xs rounded-sm font-bangla">{c}</span>
                ))}
              </div>
            </div>
          )}
          {(health?.bp_readings || []).length > 0 && (
            <div className="card-flat p-4">
              <p className="text-xs font-medium text-slate-600 font-bangla mb-2">{t('health_bp')}</p>
              <ResponsiveContainer minWidth={1} minHeight={1} width="100%" height={120}>
                <LineChart data={(health.bp_readings || []).slice().reverse()}>
                  <XAxis dataKey="date" tickFormatter={d => d.slice(5)} tick={{ fontSize: 9 }} />
                  <YAxis tick={{ fontSize: 9 }} domain={[60, 160]} />
                  <Tooltip />
                  <Line type="monotone" dataKey="systolic" stroke="#C00000" strokeWidth={2} dot={{ r: 3 }} name="Systolic" />
                  <Line type="monotone" dataKey="diastolic" stroke="#1B2A4A" strokeWidth={2} dot={{ r: 3 }} name="Diastolic" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
          {(health?.current_medications || []).length > 0 && (
            <div className="card-flat p-4">
              <p className="text-xs font-medium text-slate-600 font-bangla mb-2">{t('health_medications')}</p>
              {(health.current_medications || []).map((m, i) => (
                <div key={i} className="flex items-center gap-2 py-1">
                  <Pill size={14} className="text-[#1D9E75]" />
                  <span className="text-sm font-bangla">{m}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === 'mental' && (
        <div className="space-y-3">
          <div className="card-flat p-4">
            <h3 className="text-sm font-semibold font-bangla mb-3">{t('health_mood')}</h3>
            <p className="text-xs text-slate-500 font-bangla mb-3">{lang === 'bn' ? 'আজ আপনার মেজাজ কেমন?' : 'How are you feeling today?'}</p>
            <div className="flex justify-around">
              {moodIcons.map((Icon, i) => (
                <button key={i} data-testid={`mood-${i + 1}`} onClick={() => logMood(i + 1)}
                  className="p-3 rounded-sm hover:bg-slate-50 transition-colors active:scale-95">
                  <Icon size={32} color={moodColors[i]} />
                </button>
              ))}
            </div>
          </div>
          {moods.length > 0 && (
            <div className="card-flat p-4">
              <p className="text-xs font-medium text-slate-600 mb-2">{lang === 'bn' ? 'মেজাজের ইতিহাস' : 'Mood History'}</p>
              <ResponsiveContainer minWidth={1} minHeight={1} width="100%" height={100}>
                <BarChart data={moods.slice(0, 14).reverse()}>
                  <XAxis dataKey="date" tickFormatter={d => d.slice(8)} tick={{ fontSize: 9 }} />
                  <YAxis domain={[0, 5]} tick={{ fontSize: 9 }} />
                  <Bar dataKey="score" fill="#1D9E75" radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
          {health?.mental_wellness && (
            <div className="card-flat p-4">
              <p className="text-xs font-medium text-slate-600 font-bangla mb-2">{lang === 'bn' ? 'বার্নআউট মূল্যায়ন' : 'Burnout Assessment'}</p>
              <div className="flex items-center gap-3">
                <div className={`text-2xl font-bold ${
                  (() => { const bl = (health?.mental_wellness || {}).burnout_level || 'low'; return bl === 'low' ? 'text-emerald-600' : bl === 'moderate' ? 'text-amber-600' : 'text-red-600'; })()
                }`}>{(health?.mental_wellness || {}).last_burnout_score || 0}</div>
                <div>
                  <p className="text-xs text-slate-600 capitalize">{(health.mental_wellness || {}).burnout_level || 'low'}</p>
                  <p className="text-[10px] text-slate-400">Last: {(health.mental_wellness || {}).last_assessed || ''}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* FITNESS & ACTIVITY TRACKING */}
      {tab === 'fitness' && (
        <div className="space-y-4">
          <div className="card-surface">
            <h3 style={{fontSize:'var(--text-body)',fontWeight:600}}>{lang === 'bn' ? 'দৈনিক কার্যকলাপ ট্র্যাকার' : 'Daily Activity Tracker'}</h3>
            <div style={{fontSize:11,color:'var(--color-label-tertiary)',marginTop:2}}>{lang === 'bn' ? 'Daily Activity Tracker' : 'দৈনিক কার্যকলাপ ট্র্যাকার'}</div>
            <div className="grid grid-cols-3 gap-4" style={{marginTop:'var(--space-4)'}}>
              <div style={{textAlign:'center',padding:'var(--space-4)',background:'var(--color-accent-light)',borderRadius:'var(--radius-md)'}}>
                <div style={{fontSize:32,fontWeight:700,color:'var(--color-accent)'}}>8,450</div>
                <div style={{fontSize:'var(--text-footnote)',color:'var(--color-label-secondary)',marginTop:4}}>{lang === 'bn' ? 'আজকের পদক্ষেপ' : "Today's Steps"}</div>
                <div className="progress-track" style={{marginTop:8}}><div className="progress-fill" style={{width:'84.5%'}} /></div>
                <div style={{fontSize:10,color:'var(--color-label-tertiary)',marginTop:4}}>84.5% of 10,000</div>
              </div>
              <div style={{textAlign:'center',padding:'var(--space-4)',background:'var(--color-surface-2)',borderRadius:'var(--radius-md)'}}>
                <div style={{fontSize:32,fontWeight:700,color:'var(--color-label-primary)'}}>35</div>
                <div style={{fontSize:'var(--text-footnote)',color:'var(--color-label-secondary)',marginTop:4}}>{lang === 'bn' ? 'ব্যায়াম (মিনিট)' : 'Exercise (min)'}</div>
              </div>
              <div style={{textAlign:'center',padding:'var(--space-4)',background:'var(--color-surface-2)',borderRadius:'var(--radius-md)'}}>
                <div style={{fontSize:32,fontWeight:700,color:'var(--color-warning)'}}>7</div>
                <div style={{fontSize:'var(--text-footnote)',color:'var(--color-label-secondary)',marginTop:4}}>{lang === 'bn' ? 'দিনের ধারা' : 'Day Streak'}</div>
              </div>
            </div>
          </div>
          <div className="card-surface">
            <h4 style={{fontSize:'var(--text-subhead)',fontWeight:600,marginBottom:'var(--space-3)'}}>{lang === 'bn' ? 'প্রণোদনা মাইলস্টোন' : 'Incentive Milestones'}<span style={{display:'block',fontSize:10,color:'var(--color-label-tertiary)',marginTop:2}}>{lang === 'bn' ? 'Incentive Milestones' : 'প্রণোদনা মাইলস্টোন'}</span></h4>
            {[{id:'10k',steps:10000,en:'Baseline — Daily Target',bn:'বেসলাইন — দৈনিক লক্ষ্য',badge:'Target Met',color:'var(--color-accent)',achieved:true},{id:'15k',steps:15000,en:'Performance Incentive',bn:'কর্মক্ষমতা প্রণোদনা',badge:'Silver',color:'var(--color-warning)',achieved:false},{id:'20k',steps:20000,en:'Excellence Incentive',bn:'উৎকর্ষ প্রণোদনা',badge:'Gold',color:'#C9A84C',achieved:false}].map((m,i) => (
              <div key={i} style={{display:'flex',alignItems:'center',gap:'var(--space-3)',padding:'var(--space-3) 0',borderBottom:i<2?'1px solid var(--color-separator)':'none'}}>
                <div style={{width:36,height:36,borderRadius:'50%',background:m.achieved?m.color:'var(--color-surface-2)',display:'flex',alignItems:'center',justifyContent:'center',color:m.achieved?'white':'var(--color-label-tertiary)',fontSize:14,fontWeight:700}}>{m.achieved ? '✓' : i+1}</div>
                <div style={{flex:1}}><div style={{fontSize:'var(--text-subhead)',fontWeight:500}}>{m.steps.toLocaleString()} {lang === 'bn' ? 'পদক্ষেপ/দিন' : 'steps/day'}</div><div style={{fontSize:10,color:'var(--color-label-tertiary)',marginTop:1}}>{lang === 'bn' ? m.bn : m.en}</div></div>
                <span className={"badge " + (m.achieved ? 'badge-success' : 'badge-neutral')}>{m.badge}</span>
              </div>
            ))}
          </div>
          <div className="card-surface">
            <h4 style={{fontSize:'var(--text-subhead)',fontWeight:600,marginBottom:'var(--space-3)'}}>{lang === 'bn' ? 'সাপ্তাহিক অগ্রগতি' : 'Weekly Progress'}</h4>
            <div style={{display:'flex',gap:'var(--space-2)',justifyContent:'space-between'}}>
              {['Mon','Tue','Wed','Thu','Fri','Sat','Sun'].map((d,i) => {
                const steps = [9200,11400,8700,12100,10500,15200,8450][i];
                const pct = Math.min(steps / 15000 * 100, 100);
                return (<div key={d} style={{textAlign:'center',flex:1}}><div style={{height:80,background:'var(--color-surface-2)',borderRadius:'var(--radius-sm)',position:'relative',overflow:'hidden'}}><div style={{position:'absolute',bottom:0,left:0,right:0,height:`${pct}%`,background:steps>=10000?'var(--color-accent)':'var(--color-warning)',borderRadius:'var(--radius-sm)',transition:'height 600ms'}} /></div><div style={{fontSize:9,color:'var(--color-label-tertiary)',marginTop:4}}>{d}</div><div style={{fontSize:10,fontWeight:500}}>{(steps/1000).toFixed(1)}k</div></div>);
              })}
            </div>
          </div>
        </div>
      )}

      {/* PHYSICAL CHECKUP */}
      {tab === 'physical' && (
        <div className="space-y-4">
          <div className="card-surface">
            <h3 style={{fontSize:'var(--text-body)',fontWeight:600}}>{lang === 'bn' ? 'বিভাগীয় স্বাস্থ্য পরীক্ষা' : 'Department Health Checkup'}</h3>
            <div style={{fontSize:11,color:'var(--color-label-tertiary)',marginTop:2}}>{lang === 'bn' ? 'Department Health Checkup' : 'বিভাগীয় স্বাস্থ্য পরীক্ষা'}</div>
            <p style={{fontSize:'var(--text-subhead)',color:'var(--color-label-secondary)',marginTop:'var(--space-2)',lineHeight:1.6}}>{lang === 'bn' ? 'বিভাগ-সমর্থিত নিয়মিত স্বাস্থ্য পরীক্ষা এবং প্যাথলজিক্যাল রিপোর্ট আপলোডের মাধ্যমে স্বাস্থ্য ট্র্যাকিং।' : 'Department-supported regular health checkups with pathological report upload for health tracking over time.'}</p>
          </div>
          <div className="card-surface">
            <h4 style={{fontSize:'var(--text-subhead)',fontWeight:600,marginBottom:'var(--space-3)'}}>{lang === 'bn' ? 'সাম্প্রতিক রিপোর্ট' : 'Recent Reports'}</h4>
            {[{date:'2026-03-15',type:'Annual Health Checkup',typeBn:'বার্ষিক স্বাস্থ্য পরীক্ষা',status:'Normal',findings:'All parameters within normal range'},{date:'2026-02-01',type:'Blood Test (CBC)',typeBn:'রক্ত পরীক্ষা (CBC)',status:'Attention',findings:'Hemoglobin slightly low — 11.2 g/dL (normal: 12-16)'},{date:'2026-01-10',type:'Eye Examination',typeBn:'চোখ পরীক্ষা',status:'Normal',findings:'Vision 6/6 both eyes, no issues detected'}].map((r,i) => (
              <div key={i} style={{display:'flex',alignItems:'center',gap:'var(--space-4)',padding:'var(--space-3) 0',borderBottom:i<2?'1px solid var(--color-separator)':'none'}}>
                <div style={{flex:1}}><div style={{fontSize:'var(--text-subhead)',fontWeight:500}}>{lang === 'bn' ? r.typeBn : r.type}</div><div style={{fontSize:10,color:'var(--color-label-tertiary)',marginTop:1}}>{lang === 'bn' ? r.type : r.typeBn}</div><div style={{fontSize:'var(--text-footnote)',color:'var(--color-label-secondary)',marginTop:4}}>{r.findings}</div></div>
                <div style={{textAlign:'right',flexShrink:0}}><span className={"badge " + (r.status==='Normal'?'badge-success':'badge-warning')}>{r.status}</span><div style={{fontSize:10,color:'var(--color-label-tertiary)',marginTop:4}}>{r.date}</div></div>
              </div>
            ))}
          </div>
          <div className="card-surface" style={{borderTop:'3px solid var(--color-accent)'}}>
            <h4 style={{fontSize:'var(--text-subhead)',fontWeight:600,marginBottom:'var(--space-3)'}}>{lang === 'bn' ? 'রিপোর্ট আপলোড করুন' : 'Upload Pathological Report'}<span style={{display:'block',fontSize:10,color:'var(--color-label-tertiary)',marginTop:2}}>{lang === 'bn' ? 'Upload Report' : 'রিপোর্ট আপলোড'}</span></h4>
            <div style={{padding:'var(--space-8)',border:'2px dashed var(--color-separator-opaque)',borderRadius:'var(--radius-md)',textAlign:'center',cursor:'pointer'}}>
              <p style={{fontSize:'var(--text-subhead)',color:'var(--color-label-secondary)'}}>{lang === 'bn' ? 'রিপোর্ট ফাইল টেনে আনুন বা ক্লিক করুন' : 'Drag & drop report file or click to browse'}</p>
              <p style={{fontSize:'var(--text-caption)',color:'var(--color-label-tertiary)',marginTop:4}}>PDF, JPG, PNG — Max 10MB</p>
            </div>
            <button className="btn w-full" style={{background:'var(--color-accent)',color:'white',marginTop:'var(--space-3)'}}>{lang === 'bn' ? 'আপলোড করুন' : 'Upload Report'}</button>
          </div>
        </div>
      )}

      {/* PSYCHOLOGY COUNSELLING */}
      {tab === 'counselling' && (
        <div className="space-y-4">
          <div className="card-surface" style={{ borderLeft: '4px solid var(--color-accent)' }}>
            <h3 style={{ fontSize: 'var(--text-body)', fontWeight: 600 }}>{lang === 'bn' ? 'মনোবিজ্ঞান কাউন্সেলিং ও মানসিক স্বাস্থ্য সহায়তা' : 'Psychology Counselling & Mental Health Support'}</h3>
            <div style={{ fontSize: 11, color: 'var(--color-label-tertiary)', marginTop: 2 }}>{lang === 'bn' ? 'Psychology Counselling' : 'মনোবিজ্ঞান কাউন্সেলিং'}</div>
            <p style={{ fontSize: 'var(--text-subhead)', color: 'var(--color-label-secondary)', marginTop: 'var(--space-2)', lineHeight: 1.6 }}>{lang === 'bn' ? 'শিক্ষার্থী ও শিক্ষকদের জন্য পেশাদার মানসিক স্বাস্থ্য সহায়তা। গোপনীয় সেশন, স্ট্রেস ম্যানেজমেন্ট, এবং সংকট হস্তক্ষেপ।' : 'Professional mental health support for students and teachers. Confidential sessions, stress management, and crisis intervention.'}</p>
          </div>
          {/* Available services */}
          {[
            { en: 'Individual Student Counselling', bn: 'ব্যক্তিগত শিক্ষার্থী কাউন্সেলিং', desc: 'One-on-one sessions for students experiencing academic stress, bullying, anxiety, or behavioral challenges', status: 'available' },
            { en: 'Teacher Stress Management', bn: 'শিক্ষক স্ট্রেস ম্যানেজমেন্ট', desc: 'Burnout prevention workshops, relaxation techniques, and professional support for educators', status: 'available' },
            { en: 'Group Therapy Sessions', bn: 'গ্রুপ থেরাপি সেশন', desc: 'Peer support groups for students facing similar challenges — grief, exam anxiety, social issues', status: 'available' },
            { en: 'Parent-Student Mediation', bn: 'অভিভাবক-শিক্ষার্থী মধ্যস্থতা', desc: 'Family counselling sessions to resolve conflicts affecting student performance and wellbeing', status: 'scheduled' },
            { en: 'Crisis Intervention', bn: 'সংকট হস্তক্ষেপ', desc: 'Immediate support for emergencies — self-harm risk, severe anxiety attacks, trauma response', status: 'available' },
          ].map((service, i) => (
            <div key={i} className="card-surface" style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-4)' }}>
              <div style={{ width: 40, height: 40, borderRadius: 'var(--radius-md)', background: service.status === 'available' ? 'var(--color-accent-light)' : 'var(--color-warning-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Brain size={18} style={{ color: service.status === 'available' ? 'var(--color-accent)' : 'var(--color-warning)' }} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 'var(--text-body)', fontWeight: 600 }}>{lang === 'bn' ? service.bn : service.en}</div>
                <div style={{ fontSize: 10, color: 'var(--color-label-tertiary)', marginTop: 1 }}>{lang === 'bn' ? service.en : service.bn}</div>
                <p style={{ fontSize: 'var(--text-subhead)', color: 'var(--color-label-secondary)', marginTop: 'var(--space-2)', lineHeight: 1.5 }}>{service.desc}</p>
              </div>
              <button className="btn btn-sm" style={{ background: service.status === 'available' ? 'var(--color-accent)' : 'var(--color-surface-2)', color: service.status === 'available' ? 'white' : 'var(--color-label-tertiary)', flexShrink: 0, padding: '4px 12px', fontSize: 11 }}>
                {service.status === 'available' ? (lang === 'bn' ? 'অ্যাপয়েন্টমেন্ট' : 'Book Session') : (lang === 'bn' ? 'সময়সূচিবদ্ধ' : 'Scheduled')}
              </button>
            </div>
          ))}
          {/* Recent sessions log */}
          <div className="card-surface">
            <h4 style={{ fontSize: 'var(--text-subhead)', fontWeight: 600, marginBottom: 'var(--space-3)' }}>{lang === 'bn' ? 'সাম্প্রতিক সেশন' : 'Recent Sessions'}<span style={{ display: 'block', fontSize: 10, color: 'var(--color-label-tertiary)', marginTop: 2 }}>{lang === 'bn' ? 'Recent Sessions' : 'সাম্প্রতিক সেশন'}</span></h4>
            {[
              { date: '2026-03-25', type: 'Stress Management', typeBn: 'স্ট্রেস ম্যানেজমেন্ট', counsellor: 'Dr. Nahid Sultana', status: 'Completed' },
              { date: '2026-03-18', type: 'Individual Counselling', typeBn: 'ব্যক্তিগত কাউন্সেলিং', counsellor: 'Dr. Nahid Sultana', status: 'Completed' },
            ].map((s, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: 'var(--space-3) 0', borderBottom: i === 0 ? '1px solid var(--color-separator)' : 'none' }}>
                <div><div style={{ fontSize: 'var(--text-subhead)', fontWeight: 500 }}>{lang === 'bn' ? s.typeBn : s.type}</div><div style={{ fontSize: 10, color: 'var(--color-label-tertiary)', marginTop: 1 }}>{s.counsellor} | {s.date}</div></div>
                <span className="badge badge-success">{s.status}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === 'tele' && (
        <div className="space-y-3">
          <div className="card-flat p-4">
            <h3 className="text-sm font-semibold font-bangla mb-3">{t('health_telemedicine')}</h3>
            {!showBooking ? (
              <button data-testid="book-appointment-btn" onClick={() => setShowBooking(true)}
                className="w-full py-3 bg-[#1D9E75] text-white rounded-sm font-bangla text-sm hover:bg-emerald-600 transition-colors">
                {t('health_book')}
              </button>
            ) : (
              <div className="space-y-3">
                <select data-testid="specialty-select" value={bf.specialty}
                  onChange={e => setBf({ ...bf, specialty: e.target.value })}
                  className="w-full text-sm border border-slate-200 rounded px-3 py-2 font-bangla">
                  <option value="">{lang === 'bn' ? 'বিশেষত্ব নির্বাচন করুন' : 'Select Specialty'}</option>
                  <option value="gp">{lang === 'bn' ? 'সাধারণ চিকিৎসক' : 'General Physician'}</option>
                  <option value="gynecology">{lang === 'bn' ? 'স্ত্রীরোগ বিশেষজ্ঞ' : 'Gynecologist'}</option>
                  <option value="psychiatry">{lang === 'bn' ? 'মনোরোগ বিশেষজ্ঞ' : 'Psychiatrist'}</option>
                  <option value="dermatology">{lang === 'bn' ? 'চর্মরোগ বিশেষজ্ঞ' : 'Dermatologist'}</option>
                </select>
                <input data-testid="appointment-date" type="date" value={bf.preferred_date}
                  onChange={e => setBf({ ...bf, preferred_date: e.target.value })}
                  className="w-full text-sm border border-slate-200 rounded px-3 py-2" />
                <select data-testid="appointment-time" value={bf.preferred_time}
                  onChange={e => setBf({ ...bf, preferred_time: e.target.value })}
                  className="w-full text-sm border border-slate-200 rounded px-3 py-2 font-bangla">
                  <option value="">{lang === 'bn' ? 'সময় নির্বাচন করুন' : 'Select Time'}</option>
                  <option value="09:00">{lang === 'bn' ? 'সকাল ৯:০০' : '9:00 AM'}</option>
                  <option value="10:00">{lang === 'bn' ? 'সকাল ১০:০০' : '10:00 AM'}</option>
                  <option value="14:00">{lang === 'bn' ? 'দুপুর ২:০০' : '2:00 PM'}</option>
                  <option value="16:00">{lang === 'bn' ? 'বিকাল ৪:০০' : '4:00 PM'}</option>
                </select>
                <div className="flex gap-2">
                  <button data-testid="confirm-booking" onClick={bookAppt}
                    className="flex-1 py-2 bg-[#1D9E75] text-white rounded-sm text-sm font-bangla">{t('submit')}</button>
                  <button onClick={() => setShowBooking(false)}
                    className="flex-1 py-2 border border-slate-200 rounded-sm text-sm font-bangla">{t('cancel')}</button>
                </div>
              </div>
            )}
          </div>
          {appts.length > 0 && (
            <div className="card-flat p-4">
              <p className="text-xs font-medium text-slate-600 font-bangla mb-2">{lang === 'bn' ? 'পরামর্শের ইতিহাস' : 'Consultation History'}</p>
              {appts.map(a => (
                <div key={a.appointment_id} className="flex items-center justify-between p-2 bg-slate-50 rounded-sm mb-1">
                  <div>
                    <p className="text-xs font-bangla">{a.specialty}</p>
                    <p className="text-[10px] text-slate-400">{a.preferred_date} | {a.preferred_time}</p>
                  </div>
                  <span className={`text-[10px] px-2 py-0.5 rounded ${
                    a.status === 'pending' ? 'bg-amber-50 text-amber-600' : 'bg-emerald-50 text-emerald-600'
                  }`}>{a.status}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === 'womens' && health && health.menstrual_data && (
        <div className="card-flat p-4">
          <h3 className="text-sm font-semibold font-bangla mb-3">{t('health_womens')}</h3>
          <div className="grid grid-cols-2 gap-3">
            <div className="p-3 bg-pink-50 rounded-sm">
              <p className="text-[10px] text-pink-400">{lang === 'bn' ? 'শেষ মাসিক' : 'Last Period'}</p>
              <p className="text-sm font-medium text-pink-700">{(health.menstrual_data || {}).last_date || ''}</p>
            </div>
            <div className="p-3 bg-pink-50 rounded-sm">
              <p className="text-[10px] text-pink-400">{lang === 'bn' ? 'চক্র দৈর্ঘ্য' : 'Cycle Length'}</p>
              <p className="text-sm font-medium text-pink-700">{(health.menstrual_data || {}).cycle_length || 0} {lang === 'bn' ? 'দিন' : 'days'}</p>
            </div>
          </div>
        </div>
      )}

      {tab === 'emergency' && (
        <div className="space-y-3">
          <button data-testid="emergency-999"
            className="w-full py-6 bg-[#C00000] text-white rounded-sm flex flex-col items-center gap-2 active:scale-[0.98] transition-transform">
            <Phone size={32} />
            <span className="text-lg font-bold font-bangla">{t('health_call_999')}</span>
          </button>
          <div className="card-flat p-4">
            <p className="text-xs font-medium text-slate-600 font-bangla mb-1">{lang === 'bn' ? 'নিকটতম হাসপাতাল' : 'Nearest Hospital'}</p>
            <p className="text-sm font-bangla">{lang === 'bn' ? 'ঢাকা মেডিকেল কলেজ হাসপাতাল' : 'Dhaka Medical College Hospital'}</p>
            <p className="text-xs text-slate-400">{lang === 'bn' ? 'দূরত্ব: ২.৫ কিমি' : 'Distance: 2.5 km'}</p>
          </div>
          <div className="card-flat p-4">
            <p className="text-xs font-medium text-slate-600 font-bangla mb-1">{lang === 'bn' ? 'ক্রাইসিস লাইন' : 'Crisis Line'}</p>
            <p className="text-sm font-bangla">{lang === 'bn' ? 'কান পেতে রই: 01779-554391' : 'Kaan Pete Roi: 01779-554391'}</p>
            <p className="text-xs text-slate-400">{lang === 'bn' ? 'সকাল ৮টা - রাত ১০টা' : '8 AM - 10 PM'}</p>
          </div>
        </div>
      )}
    </div>
  );
}

function HealthAdminView() {
  const { lang } = useApp();
  return (
    <div className="space-y-6 animate-fade-in" data-testid="health-admin-view">
      <div className="card-flat p-8 text-center">
        <Lock size={48} className="text-slate-300 mx-auto mb-3" />
        <h3 className="text-lg font-semibold text-slate-700 font-english">{lang === 'bn' ? 'ব্যক্তিগত স্বাস্থ্য রেকর্ড প্রবেশযোগ্য নয়' : 'Individual Health Records Not Accessible'}</h3>
        <p className="text-sm text-slate-500 mt-2 max-w-md mx-auto">
          {lang === 'bn'
            ? 'শিক্ষকদের ব্যক্তিগত স্বাস্থ্য তথ্য শুধুমাত্র তাদের নিজস্ব। কোনো প্রশাসক এই তথ্য দেখতে পারবেন না।'
            : 'Individual teacher health records are strictly private. No administrator can access personal health data. Only aggregate anonymised statistics are available.'}
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="kpi-card">
          <p className="text-xs uppercase tracking-wider text-slate-400 font-english mb-1">{lang === 'bn' ? 'PHR সহ শিক্ষক' : 'Teachers with PHR'}</p>
          <p className="kpi-value text-[#1D9E75]">6</p>
          <p className="text-[10px] text-slate-400 mt-1">{lang === 'bn' ? '৬৬৭,০৯৯ জন শিক্ষকের মধ্যে (BANBEIS ২০২৪)' : 'Out of 667,099 total teachers (BANBEIS 2024)'}</p>
        </div>
        <div className="kpi-card">
          <p className="text-xs uppercase tracking-wider text-slate-400 font-english mb-1">{lang === 'bn' ? 'টেলিমেডিসিন সেশন' : 'Telemedicine Sessions'}</p>
          <p className="kpi-value text-[#1B2A4A]">--</p>
          <p className="text-[10px] text-slate-400 mt-1">{lang === 'bn' ? 'শুধু সমষ্টিগত' : 'Aggregated only'}</p>
        </div>
        <div className="kpi-card">
          <p className="text-xs uppercase tracking-wider text-slate-400 font-english mb-1">{lang === 'bn' ? 'গড় বার্নআউট স্কোর' : 'Avg Burnout Score'}</p>
          <p className="kpi-value text-[#C9A84C]">--</p>
          <p className="text-[10px] text-slate-400 mt-1">{lang === 'bn' ? 'ন্যূনতম গ্রুপ: ১০' : 'Min group size: 10'}</p>
        </div>
      </div>
      <div className="border border-amber-200 bg-amber-50 p-4 rounded-sm flex items-start gap-2">
        <AlertTriangle size={16} className="text-amber-600 mt-0.5" />
        <p className="text-xs text-amber-700 font-bangla">
          {lang === 'bn' ? 'গোপনীয়তা নীতি: ন্যূনতম ১০ জনের গ্রুপ ছাড়া কোনো স্বাস্থ্য পরিসংখ্যান প্রদর্শিত হবে না।' : 'Privacy policy: No health statistics will be displayed for groups smaller than 10.'}
        </p>
      </div>
    </div>
  );
}

export default function Health() {
  const { isTeacher } = useApp();
  return isTeacher ? <HealthTeacherView /> : <HealthAdminView />;
}
